import { Component, forwardRef, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { TextInputComponent } from '../text-input/text-input.component';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectOption } from '../../model/select.option';
import { MatSelect, MatSelectChange } from '@angular/material/select';

@Component({
  selector: 'app-select-input',
  templateUrl: './select-input.component.html',
  styleUrls: ['./select-input.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SelectInputComponent),
    multi: true
  }]
})

export class SelectInputComponent extends TextInputComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  constructor(private elementRef: ElementRef) { super(); }

  // Abre o painel do mat-select assim que o componente é criado. Útil quando o
  // select só existe após uma ação (ex.: "editar sob clique" na sugestão de
  // atribuição) — evita um 2º clique só p/ abrir. Default false (demais usos
  // não são afetados).
  @Input() autoOpen = false;
  @ViewChild('selectInput') private matSelectView?: MatSelect;

  @Input() options;
  @Input() indices = {
    internal: 'id',
    public: 'value'
  };

  // Chave de `indices` usada como campo de EXIBIÇÃO das opções não-agrupadas
  // (branch `#selectCountry` do template). Sem este default a expressão
  // `indices[indicesFilter]` resolvia `indices[undefined]` → `undefined` e as
  // opções planas (trilhas, grupos, tópicos sem subtópicos) saíam em branco.
  // Mesmo default de `app-jems-select`/`app-select-input-group` ('public').
  @Input() indicesFilter = 'public';

  @Input() prependPublic = '';
  @Output() selectionChange: EventEmitter<MatSelectChange> = new EventEmitter<MatSelectChange>();
  @Output() inputValueChange: EventEmitter<string> = new EventEmitter<string>();

  @Input() multiple = false;

  @Input() compact = false;
  @Input() includeNoneOption = false;
  @Input() disabled = false;
  // Classe(s) aplicada(s) ao painel do overlay do mat-select (panelClass). Permite
  // estilizar o dropdown via CSS global (ex.: 'jems3-select-panel' = padrão de menu JEMS3).
  @Input() panelClass = '';

  // Modo CHIP de status (filtro de status da Lista de Submissões): renderiza cada
  // opção — e o valor selecionado no gatilho — como chip de status de submissão.
  // As options precisam carregar { statusClass, icon, label } (vide field-filter-v2).
  // Default false: nenhum outro uso do app-select-input é afetado.
  @Input() statusChip = false;

  valueInputSelected: string;

  @Input() LastOptionBold = false;
  @Input() tooltips: { disabled?: string } = { disabled: null };

  ngOnChanges(): void {
    this.ngOnInit();
    this.syncDisabledState();
  }

  ngOnInit() {
    this.valueInputSelected = this.formGroupController.controls[this.name].value;
    // Chrome JEMS3 (`jems3-select-panel`) auto-injetado em TODO `app-select-input`
    // que não passou panelClass próprio — tokens/hover/dark/check SF-style + tipografia
    // 13px unificadas.
    if (!this.panelClass) {
      this.panelClass = 'jems3-select-panel';
    }
    this.syncDisabledState();
  }

  // Apply the `disabled` @Input by calling enable()/disable() on the
  // bound FormControl — recommended pattern for reactive forms. Avoids
  // the `[disabled]` template binding on the form directive which
  // Angular warns about (and which can trigger NG0100 errors).
  private syncDisabledState(): void {
    const ctrl = this.formGroupController?.controls?.[this.name];
    if (!ctrl) return;
    if (this.disabled && !ctrl.disabled) {
      ctrl.disable({ emitEvent: false });
    } else if (!this.disabled && ctrl.disabled) {
      ctrl.enable({ emitEvent: false });
    }
  }

  ngAfterViewInit(): void {
    // setTimeout (não microtask) para o open() não colidir com o clique que
    // acabou de criar este componente (o listener de document click fecharia).
    if (this.autoOpen) {
      setTimeout(() => this.matSelectView?.open());
    }
  }

  // Opções selecionadas (modo statusChip) para montar os chips do gatilho. Lê o
  // valor do form (array no múltiplo) e casa com as options pela chave interna.
  get selectedStatusChips(): Array<any> {
    if (!this.statusChip || !this.options) { return []; }
    const raw = this.formGroupController?.controls?.[this.name]?.value;
    const selected = Array.isArray(raw) ? raw : (raw != null ? [raw] : []);
    return this.options.filter(o => selected.indexOf(o[this.indices.internal]) !== -1);
  }

  onSelection($event: MatSelectChange): void {
    this.valueInputSelected = $event.value;
    this.selectionChange.emit($event);
  }

  // Fecha o painel ao clicar FORA dele (em qualquer lugar), incl. a própria
  // caixa. O backdrop nativo do mat-select não fecha de forma confiável neste
  // contexto (caixa dentro de container sticky), então usamos um listener de
  // `mousedown` no DOCUMENTO em fase de CAPTURA — dispara antes de tudo — e só
  // enquanto o painel está aberto (via openedChange). Cliques DENTRO do painel
  // (seleção de opção) são ignorados; o mat-select trata.
  private matSelect: { panelOpen?: boolean; close?: () => void } | null = null;
  private listenersAdded = false;
  private suppressClick = false;

  // Registra os listeners de documento UMA vez (no 1º open) e os mantém — eles
  // só agem quando o painel está aberto (early-return). Evita a corrida de
  // remover no openedChange durante o próprio close().
  onPanelToggle(opened: boolean, sel: { panelOpen?: boolean; close?: () => void }): void {
    this.matSelect = sel;
    if (opened && !this.listenersAdded) {
      document.addEventListener('mousedown', this.onDocPointer, true);
      document.addEventListener('click', this.onDocClick, true);
      this.listenersAdded = true;
    }
  }

  private onDocPointer = (e: MouseEvent): void => {
    const sel = this.matSelect;
    if (!sel || !sel.panelOpen) { return; }
    const targetEl = e.target as HTMLElement;
    // Clique DENTRO do painel (opção, busca) → deixa o mat-select tratar. NOTA:
    // o `.cdk-overlay-pane` (wrapper) cobre a área da CAIXA, mas NÃO é o painel
    // (`.mat-select-panel`) — então clicar na caixa cai aqui como "fora" e fecha.
    if (targetEl && targetEl.closest && targetEl.closest('.mat-select-panel')) { return; }
    if (sel.close) { sel.close(); }
    // Se o alvo é a PRÓPRIA caixa (trigger), impede o mat-select de reabrir no
    // mousedown/click seguinte. (Quando o alvo é o pane/backdrop, o close já
    // basta — o evento não chega ao trigger.)
    if (targetEl && this.elementRef.nativeElement.contains(targetEl)) {
      e.preventDefault();
      e.stopPropagation();
      this.suppressClick = true;
    }
  };

  private onDocClick = (e: MouseEvent): void => {
    if (this.suppressClick) { e.stopPropagation(); e.preventDefault(); this.suppressClick = false; }
  };

  ngOnDestroy(): void {
    if (this.listenersAdded) {
      document.removeEventListener('mousedown', this.onDocPointer, true);
      document.removeEventListener('click', this.onDocClick, true);
    }
  }

  setOptionTooltip(option): string {
    if (this.tooltips.disabled && option?.disabled) {
      return this.tooltips.disabled;
    }

    return null;
  }

  tooltipTimezoneContent(): string {
    if (this.name === 'timeZone' && !!this.valueInputSelected) {
      const tooltip = this.options.filter((option) => option[this.indices.public] === this.valueInputSelected)[0];
      return tooltip.filter;
    }
  }

  getValueInputCity($value: any): void {
    if ($value.length >= 3) {
      this.inputValueChange.emit($value);
    }
  }

  setClassCountryWarning() {
    const componentGeneratingNow: string = this.indices.internal;
    const countryIsDefined = this.formGroupController.controls?.country?.value;

    if (componentGeneratingNow === 'isoCode' && countryIsDefined === null) {
      return 'country-warning';
    }
  }
}
